import { defineComponent as _defineComponent } from 'vue'
import { createElementVNode as _createElementVNode, unref as _unref, createVNode as _createVNode, createTextVNode as _createTextVNode, openBlock as _openBlock, createElementBlock as _createElementBlock, createCommentVNode as _createCommentVNode, vModelText as _vModelText, withDirectives as _withDirectives, renderList as _renderList, Fragment as _Fragment, withModifiers as _withModifiers, toDisplayString as _toDisplayString, withCtx as _withCtx, createBlock as _createBlock } from "vue"

const _hoisted_1 = { class: "max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8" }
const _hoisted_2 = { class: "flex items-center justify-between mb-6" }
const _hoisted_3 = { class: "mb-6 bg-white border border-gray-200 rounded-xl p-4 shadow-sm" }
const _hoisted_4 = { class: "relative" }
const _hoisted_5 = {
  key: 0,
  class: "flex items-center justify-center py-12 bg-white border border-gray-200 rounded-xl"
}
const _hoisted_6 = {
  key: 1,
  class: "grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4"
}
const _hoisted_7 = ["onClick"]
const _hoisted_8 = { class: "flex items-start justify-between mb-4" }
const _hoisted_9 = { class: "p-3 bg-gray-50 rounded-lg group-hover:bg-[#22282F] group-hover:text-white transition-colors" }
const _hoisted_10 = { class: "flex gap-2" }
const _hoisted_11 = ["onClick"]
const _hoisted_12 = ["onClick"]
const _hoisted_13 = { class: "text-lg font-semibold text-[#22282F] mb-1" }
const _hoisted_14 = { class: "text-sm text-gray-500 mb-4" }
const _hoisted_15 = { class: "flex items-center justify-between pt-4 border-t border-gray-100" }
const _hoisted_16 = { class: "flex items-center gap-2 text-sm text-gray-600" }
const _hoisted_17 = {
  key: 2,
  class: "bg-white border border-gray-200 rounded-xl p-12 text-center"
}
const _hoisted_18 = { class: "text-sm text-gray-500" }
const _hoisted_19 = { class: "bg-white rounded-xl max-w-md w-full p-6" }
const _hoisted_20 = { class: "text-lg font-bold text-[#22282F] mb-4" }
const _hoisted_21 = { class: "space-y-4" }
const _hoisted_22 = { class: "flex gap-3 mt-6" }

import { ref, computed, onMounted } from 'vue'
import { useRouter } from 'vue-router'
import { Building2, Layers, Plus, Edit3, Trash2, Search, ChevronRight } from 'lucide-vue-next'
import AdminLayout from '@/layouts/AdminLayout.vue'
import { apiFetch } from '@/utils/apiFetch'
import { useToast } from '@/utils/toast'
import { useAuthStore } from '@/store/auth'

interface Direction {
  id: number
  code: string
  nom: string
  departements_count?: number
}


export default /*@__PURE__*/_defineComponent({
  __name: 'DirectionsListView',
  setup(__props) {

const toast = useToast()
const authStore = useAuthStore()

const router = useRouter()
const loading = ref(false)
const directions = ref<Direction[]>([])
const searchQuery = ref('')
const showModal = ref(false)
const editingDirection = ref<Direction | null>(null)
const form = ref({ code: '', nom: '' })

const isDirectionScopedAdmin = computed(() => {
  const roleCode = authStore.user?.role?.code
  return !!authStore.user?.direction && (roleCode === 'ADMIN' || roleCode === 'DIRECTEUR')
})

const canDeleteDirection = (direction: Direction) => {
  const roleCode = authStore.user?.role?.code
  const adminDirectionId = authStore.user?.direction?.id
  if (roleCode === 'DIRECTEUR' && adminDirectionId === direction.id) {
    return false
  }
  return true
}

const filteredDirections = computed(() => {
  if (!searchQuery.value) return directions.value
  const query = searchQuery.value.toLowerCase()
  return directions.value.filter(d =>
    d.nom.toLowerCase().includes(query) || d.code.toLowerCase().includes(query)
  )
})

const fetchDirections = async () => {
  loading.value = true
  try {
    directions.value = await apiFetch('/api/admin/directions/')
  } catch (error: any) {
    toast.error('Erreur', 'Impossible de charger les directions')
  } finally {
    loading.value = false
  }
}

const openCreateModal = () => {
  editingDirection.value = null
  form.value = { code: '', nom: '' }
  showModal.value = true
}

const openEditModal = (direction: Direction) => {
  editingDirection.value = direction
  form.value = { code: direction.code, nom: direction.nom }
  showModal.value = true
}

const closeModal = () => {
  showModal.value = false
  editingDirection.value = null
}

const saveDirection = async () => {
  try {
    const url = editingDirection.value
      ? `/api/admin/directions/${editingDirection.value.id}/update/`
      : '/api/admin/directions/create/'

    await apiFetch(url, {
      method: editingDirection.value ? 'PUT' : 'POST',
      body: JSON.stringify(form.value)
    })

    toast.success(
      editingDirection.value ? 'Direction modifiée' : 'Direction créée',
      'L\'opération a réussi'
    )
    await fetchDirections()
    closeModal()
  } catch (error: any) {
    toast.error('Erreur', error.message || 'Impossible de sauvegarder la direction')
  }
}

const deleteDirection = async (id: number) => {
  const target = directions.value.find(direction => direction.id === id)
  if (target && !canDeleteDirection(target)) {
    toast.error('Action non autorisée', 'Un directeur ne peut pas supprimer sa direction')
    return
  }

  try {
    await apiFetch(`/api/admin/directions/${id}/delete/`, {
      method: 'DELETE'
    })

    toast.success('Direction supprimée', 'La direction a été supprimée avec succès')
    await fetchDirections()
  } catch (error: any) {
    toast.error('Erreur', error.message || 'Impossible de supprimer la direction')
  }
}

const goToDepartements = (direction: Direction) => {
  router.push(`/organisation/directions/${direction.id}/departements`)
}

onMounted(() => {
  fetchDirections()
})

return (_ctx: any,_cache: any) => {
  return (_openBlock(), _createBlock(AdminLayout, null, {
    default: _withCtx(() => [
      _createElementVNode("div", _hoisted_1, [
        _createElementVNode("div", _hoisted_2, [
          _cache[4] || (_cache[4] = _createElementVNode("h2", { class: "text-2xl font-bold text-[#22282F]" }, "Directions", -1)),
          (!isDirectionScopedAdmin.value)
            ? (_openBlock(), _createElementBlock("button", {
                key: 0,
                onClick: openCreateModal,
                class: "px-4 py-2 bg-[#22282F] text-white rounded-lg hover:bg-[#22282F]/90 transition-colors flex items-center gap-2"
              }, [
                _createVNode(_unref(Plus), { class: "w-4 h-4" }),
                _cache[3] || (_cache[3] = _createTextVNode(" Nouvelle Direction "))
              ]))
            : _createCommentVNode("", true)
        ]),
        _createElementVNode("div", _hoisted_3, [
          _createElementVNode("div", _hoisted_4, [
            _createVNode(_unref(Search), { class: "absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-gray-400" }),
            _withDirectives(_createElementVNode("input", {
              "onUpdate:modelValue": _cache[0] || (_cache[0] = ($event: any) => ((searchQuery).value = $event)),
              type: "text",
              placeholder: "Rechercher une direction par nom ou code...",
              class: "w-full pl-12 pr-4 py-3 bg-white border border-gray-200 rounded-lg text-sm focus:outline-none focus:border-[#22282F] focus:ring-2 focus:ring-[#22282F]/20"
            }, null, 512), [
              [_vModelText, searchQuery.value]
            ])
          ])
        ]),
        (loading.value)
          ? (_openBlock(), _createElementBlock("div", _hoisted_5, _cache[5] || (_cache[5] = [
              _createElementVNode("div", { class: "w-8 h-8 border-4 border-gray-200 border-t-[#22282F] rounded-full animate-spin" }, null, -1)
            ])))
          : (filteredDirections.value.length)
            ? (_openBlock(), _createElementBlock("div", _hoisted_6, [
                (_openBlock(true), _createElementBlock(_Fragment, null, _renderList(filteredDirections.value, (direction) => {
                  return (_openBlock(), _createElementBlock("div", {
                    key: direction.id,
                    class: "bg-white border border-gray-200 rounded-xl p-6 hover:shadow-lg transition-all cursor-pointer group",
                    onClick: ($event: any) => (goToDepartements(direction))
                  }, [
                    _createElementVNode("div", _hoisted_8, [
                      _createElementVNode("div", _hoisted_9, [
                        _createVNode(_unref(Building2), { class: "w-6 h-6" })
                      ]),
                      _createElementVNode("div", _hoisted_10, [
                        _createElementVNode("button", {
                          onClick: _withModifiers(($event: any) => (openEditModal(direction)), ["stop"]),
                          class: "p-2 hover:bg-gray-100 rounded-lg transition-colors",
                          title: "Modifier"
                        }, [
                          _createVNode(_unref(Edit3), { class: "w-4 h-4 text-gray-600" })
                        ], 8, _hoisted_11),
                        (canDeleteDirection(direction))
                          ? (_openBlock(), _createElementBlock("button", {
                              key: 0,
                              onClick: _withModifiers(($event: any) => (deleteDirection(direction.id)), ["stop"]),
                              class: "p-2 hover:bg-red-50 rounded-lg transition-colors",
                              title: "Supprimer"
                            }, [
                              _createVNode(_unref(Trash2), { class: "w-4 h-4 text-red-600" })
                            ], 8, _hoisted_12))
                          : _createCommentVNode("", true)
                      ])
                    ]),
                    _createElementVNode("h3", _hoisted_13, _toDisplayString(direction.nom), 1),
                    _createElementVNode("p", _hoisted_14, _toDisplayString(direction.code), 1),
                    _createElementVNode("div", _hoisted_15, [
                      _createElementVNode("div", _hoisted_16, [
                        _createVNode(_unref(Layers), { class: "w-4 h-4" }),
                        _cache[6] || (_cache[6] = _createElementVNode("span", null, "Département(s)", -1))
                      ]),
                      _createVNode(_unref(ChevronRight), { class: "w-5 h-5 text-gray-400 group-hover:text-[#22282F] transition-colors" })
                    ])
                  ], 8, _hoisted_7))
                }), 128))
              ]))
            : (_openBlock(), _createElementBlock("div", _hoisted_17, [
                _createVNode(_unref(Building2), { class: "w-16 h-16 text-gray-300 mx-auto mb-4" }),
                _cache[7] || (_cache[7] = _createElementVNode("h3", { class: "text-lg font-semibold text-[#22282F] mb-2" }, "Aucune direction trouvée", -1)),
                _createElementVNode("p", _hoisted_18, _toDisplayString(searchQuery.value ? 'Essayez de modifier votre recherche' : 'Créez votre première direction pour commencer'), 1)
              ])),
        (showModal.value)
          ? (_openBlock(), _createElementBlock("div", {
              key: 3,
              class: "fixed inset-0 bg-black/50 flex items-center justify-center p-4 z-50",
              onClick: _withModifiers(closeModal, ["self"])
            }, [
              _createElementVNode("div", _hoisted_19, [
                _createElementVNode("h3", _hoisted_20, _toDisplayString(editingDirection.value ? 'Modifier la direction' : 'Nouvelle direction'), 1),
                _createElementVNode("div", _hoisted_21, [
                  _createElementVNode("div", null, [
                    _cache[8] || (_cache[8] = _createElementVNode("label", { class: "block text-sm font-medium text-[#22282F] mb-2" }, "Code", -1)),
                    _withDirectives(_createElementVNode("input", {
                      "onUpdate:modelValue": _cache[1] || (_cache[1] = ($event: any) => ((form.value.code) = $event)),
                      type: "text",
                      class: "w-full px-3 py-2 border border-gray-200 rounded-lg focus:outline-none focus:border-[#22282F] focus:ring-2 focus:ring-[#22282F]/20",
                      placeholder: "ex: DIR-01"
                    }, null, 512), [
                      [_vModelText, form.value.code]
                    ])
                  ]),
                  _createElementVNode("div", null, [
                    _cache[9] || (_cache[9] = _createElementVNode("label", { class: "block text-sm font-medium text-[#22282F] mb-2" }, "Nom", -1)),
                    _withDirectives(_createElementVNode("input", {
                      "onUpdate:modelValue": _cache[2] || (_cache[2] = ($event: any) => ((form.value.nom) = $event)),
                      type: "text",
                      class: "w-full px-3 py-2 border border-gray-200 rounded-lg focus:outline-none focus:border-[#22282F] focus:ring-2 focus:ring-[#22282F]/20",
                      placeholder: "ex: Direction Générale"
                    }, null, 512), [
                      [_vModelText, form.value.nom]
                    ])
                  ])
                ]),
                _createElementVNode("div", _hoisted_22, [
                  _createElementVNode("button", {
                    onClick: closeModal,
                    class: "flex-1 px-4 py-2 border border-gray-200 rounded-lg hover:bg-gray-50 transition-colors"
                  }, " Annuler "),
                  _createElementVNode("button", {
                    onClick: saveDirection,
                    class: "flex-1 px-4 py-2 bg-[#22282F] text-white rounded-lg hover:bg-[#22282F]/90 transition-colors"
                  }, _toDisplayString(editingDirection.value ? 'Modifier' : 'Créer'), 1)
                ])
              ])
            ]))
          : _createCommentVNode("", true)
      ])
    ]),
    _: 1
  }))
}
}

})